import {ChevronDownIcon, InformationCircleIcon} from '@heroicons/react/24/solid';
import {debounce, take} from 'lodash';
import {DateTime} from 'luxon';
import React from 'react';
import {Tooltip} from 'react-tooltip';
import {useStateWithDeps} from 'use-state-with-deps';

import {ForecastTimesState} from 'src/hooks/useSelectedForecastTimes';
import {Site} from 'src/lib/api/project';
import {ForecastTime, Source} from 'src/lib/api/series';
import {SERIES_PROCESSOR_IDS_TO_INIT_TIME_OFFSET_MINUTES} from 'src/lib/constants';
import {formatDateShortFull} from 'src/lib/dates';
import {makeDateFromString, makeStringFromDate} from 'src/lib/siteUtils';
import {ConfigBySourceId} from 'src/lib/sourceConfigs';
import * as tw from 'src/lib/tailwindClasses';

import {recordEvent} from './Analytics';
import LoadingSpinner from './LoadingSpinner';
import {useTimezone} from './TimezoneContext';
import {useTranslations} from './TranslationsContext';

type Props = {
  horizon: number;
  maxSelectedForecasts?: number;
  site: Site;
  dateRange: [Date, Date];
  defaultDateRange: [Date, Date];
  setDateRange: (dateRange: [Date, Date]) => void;
  shortcuts: {
    absolute?: Shortcut[];
    relative?: Shortcut[];
  };
} & Pick<
  ForecastTimesState,
  | 'primaryForecastTimes'
  | 'selectedPrimaryForecastTimes'
  | 'setSelectedPrimaryForecastTimes'
  | 'onNextPrimaryForecastTimesChange'
>;

export default function ForecastSelect({
  horizon,
  maxSelectedForecasts,
  site,
  dateRange,
  setDateRange,
  defaultDateRange,
  shortcuts,
  primaryForecastTimes,
  selectedPrimaryForecastTimes,
  setSelectedPrimaryForecastTimes,
  onNextPrimaryForecastTimesChange,
}: Props) {
  const {t} = useTranslations();
  const {getDisplayTimeZone} = useTimezone();
  const displayTimeZone = getDisplayTimeZone(site.localTimeZone);
  const [startDate, endDate] = dateRange;
  const pickerId = React.useId();
  const selectionLimitReached =
    maxSelectedForecasts !== undefined &&
    selectedPrimaryForecastTimes.length >= maxSelectedForecasts;

  /* Synchronize local start date with external start date. */
  const [localStartDateString, setLocalStartDateString] = useStateWithDeps(
    makeStringFromDate(startDate, site.localTimeZone),
    [startDate, site]
  );

  /* Synchronize local end date with external end date. */
  const [localEndDateString, setLocalEndDateString] = useStateWithDeps(
    makeStringFromDate(endDate, site.localTimeZone),
    [endDate, site]
  );

  const [startDateErrorText, setStartDateErrorText] = useStateWithDeps('', [
    site,
    startDate,
    endDate,
  ]);

  const [endDateErrorText, setEndDateErrorText] = useStateWithDeps('', [site, startDate, endDate]);

  const debouncedSetStartDateErrorText = debounce(setStartDateErrorText, 500);
  const debouncedSetEndDateErrorText = debounce(setEndDateErrorText, 500);
  const debouncedSetDateRange = debounce(setDateRange, 500);

  const handleNewStartDate = (dateString: string) => {
    setLocalStartDateString(dateString);
    const minStartDate = makeDateFromString('2017-01-01', site.localTimeZone);
    const newStartDate = makeDateFromString(dateString, site.localTimeZone);
    let startDateErrorText = '';
    if (!newStartDate.getTime()) {
      startDateErrorText = t('invalidDate');
    } else if (newStartDate > endDate) {
      startDateErrorText = t('startDateBeforeEndDate');
    } else if (newStartDate < minStartDate) {
      startDateErrorText = t('minimumStartDate', {date: makeDateInputStringFromDate(minStartDate)});
    } else if (newStartDate.getTime() !== startDate.getTime()) {
      debouncedSetDateRange([newStartDate, endDate]);
      recordEvent('date_range_modified', {method: 'manual'});
    }
    debouncedSetStartDateErrorText(startDateErrorText);
  };

  const handleNewEndDate = (dateString: string) => {
    setLocalEndDateString(dateString);
    const newEndDate = makeDateFromString(dateString, site.localTimeZone, true);
    let endDateErrorText = '';
    if (!newEndDate.getTime()) {
      endDateErrorText = t('invalidDate');
    } else if (newEndDate < startDate) {
      endDateErrorText = t('endDateAfterStartDate');
    } else if (endDate.getTime() && newEndDate.getTime() !== endDate.getTime()) {
      debouncedSetDateRange([startDate, newEndDate]);
      recordEvent('date_range_modified', {method: 'manual'});
    }
    debouncedSetEndDateErrorText(endDateErrorText);
  };

  let forecastTimeText = t('noForecastsAvailable');
  if (primaryForecastTimes) {
    forecastTimeText = selectedPrimaryForecastTimes.length
      ? `${selectedPrimaryForecastTimes.length} ${selectedPrimaryForecastTimes.length === 1 ? t('forecast') : t('forecasts')}`
      : t('noForecastsSelected');
  }

  return (
    <div className="my-2">
      <div className="flex items-center relative flex-wrap gap-2">
        {shortcuts.absolute && (
          <div className="mr-4 flex items-center">
            <Shortcuts
              defaultDateRange={defaultDateRange}
              site={site}
              setDateRange={setDateRange}
              setSelectedForecastTimes={setSelectedPrimaryForecastTimes}
              onNextPrimaryForecastTimesChange={onNextPrimaryForecastTimesChange}
              forecastTimes={primaryForecastTimes}
              shortcuts={shortcuts.absolute}
            />
            <div className="border-r border-gray-200 h-8 ml-4" />
          </div>
        )}
        {/* Each input owns the containing block for its own error text. Absolutely
            positioned children of a flex container all share one static position, so
            anchoring both messages to the row stacks them on top of each other. */}
        <div className="relative flex">
          <input
            className={`h-8 w-32 px-2 py-1 rounded-md ${tw.ringBorder}`}
            type="date"
            value={localStartDateString}
            onChange={(e) => handleNewStartDate(e.target.value)}
            min="2017-01-01"
            max={makeStringFromDate(
              DateTime.now().plus({days: horizon}).toJSDate(),
              site.localTimeZone
            )}
          />
          {startDateErrorText && (
            <p className="absolute left-0 top-full mt-2 text-red-500 text-xs w-32">
              {startDateErrorText}
            </p>
          )}
        </div>
        <p>{t('to')}</p>
        <div className="relative flex">
          <input
            className={`h-8 w-32 px-2 py-1 rounded-md ${tw.ringBorder}`}
            type="date"
            value={localEndDateString}
            onChange={(e) => handleNewEndDate(e.target.value)}
            min={makeStringFromDate(startDate, site.localTimeZone)}
          />
          {endDateErrorText && (
            <p className="absolute left-0 top-full mt-2 text-red-500 text-xs w-32">
              {endDateErrorText}
            </p>
          )}
        </div>
        <div
          className={`group relative min-w-52 min-h-full whitespace-nowrap ${!primaryForecastTimes && 'pointer-events-none'}`}
        >
          <div
            className={`w-full flex items-center justify-between z-30 px-2 py-1 gap-x-2 gap-y-1 ${tw.ringBorder} group-hover:rounded-b-none`}
          >
            <div>{forecastTimeText}</div>
            {primaryForecastTimes && <ChevronDownIcon className="h-4 w-4 pt-1" />}
          </div>
          <div
            className={`w-full ${tw.showOnGroupHover} ${tw.floatingDropdown} z-20 min-w-fit right-0 group-hover:rounded-t-none`}
          >
            <div className="overflow-y-auto h-56 px-4">
              <div className="py-1 flex flex-col gap-y-1">
                {shortcuts.relative && (
                  <>
                    <div className="font-bold">{t('shortcuts')}</div>
                    <Shortcuts
                      defaultDateRange={defaultDateRange}
                      site={site}
                      setDateRange={setDateRange}
                      setSelectedForecastTimes={setSelectedPrimaryForecastTimes}
                      onNextPrimaryForecastTimesChange={onNextPrimaryForecastTimesChange}
                      forecastTimes={primaryForecastTimes}
                      shortcuts={shortcuts.relative}
                    />
                    <hr className="my-2" />
                  </>
                )}

                <div className="flex items-center justify-between">
                  <div className="font-bold">{t('allForecasts')}</div>

                  <button
                    className="text-sm text-blue-600"
                    disabled={!primaryForecastTimes}
                    onClick={() => {
                      setSelectedPrimaryForecastTimes([]);
                    }}
                  >
                    {t('clear')}
                  </button>
                </div>
              </div>
              {selectionLimitReached && (
                <p role="status" className="text-sm text-gray-500 py-1">
                  {t('forecastSelectionLimit', {count: String(maxSelectedForecasts)})}
                </p>
              )}
              {primaryForecastTimes &&
                [...primaryForecastTimes]
                  .sort((a, b) => (a.initializationTime > b.initializationTime ? -1 : 1))
                  .map((ft, i) => {
                    const isSelected = !!selectedPrimaryForecastTimes.find(
                      (ftB) => ft.initializationTime.getTime() === ftB.initializationTime.getTime()
                    );

                    // Several pickers are mounted at once, and a label's htmlFor is document-wide.
                    const id = `${pickerId}-${i}`;

                    const updateTimeMissingFromLabel = getUpdateTimeMissingFromLabel(ft);

                    return (
                      <div key={i} className="group/forecast-time whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="mr-2"
                          id={id}
                          name="scales"
                          checked={isSelected}
                          disabled={!isSelected && selectionLimitReached}
                          onChange={(e) => {
                            if (isSelected) {
                              const newSelection = selectedPrimaryForecastTimes.filter(
                                (forecastTime) =>
                                  forecastTime.initializationTime.getTime() !==
                                  ft.initializationTime.getTime()
                              );
                              setSelectedPrimaryForecastTimes(newSelection);
                              recordEvent('forecast_selected', {count: newSelection.length});
                            } else {
                              const newSelection = [...selectedPrimaryForecastTimes, ft];
                              setSelectedPrimaryForecastTimes(newSelection);
                              recordEvent('forecast_selected', {count: newSelection.length});
                            }
                          }}
                        />
                        <label htmlFor={id}>
                          {formatDateShortFull(ft.issueTime, displayTimeZone)}{' '}
                          {updateTimeMissingFromLabel && (
                            <span className="text-gray-400">
                              {formatDateShortFull(updateTimeMissingFromLabel, displayTimeZone)}
                            </span>
                          )}
                          <ForecastTimeInfo forecastTime={ft} displayTimeZone={displayTimeZone} />
                        </label>
                      </div>
                    );
                  })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// The row is labelled with `issueTime`, which the API replaces with the initialization time for
// forecasts published long after they initialized, so on its own the label can leave out when the
// forecast actually became available. Rows whose tooltip already dates the forecast (see
// WeatherInputTimesTable) don't need it spelled out.
function getUpdateTimeMissingFromLabel(forecastTime: ForecastTime): Date | undefined {
  const {inputInitializationTimes, issueTime, lastUpdatedTime} = forecastTime;
  if (inputInitializationTimes || lastUpdatedTime?.getTime() === issueTime.getTime()) {
    return undefined;
  }
  return lastUpdatedTime;
}

// The "i" icon and its tooltip only show while the mouse is over the row.
function ForecastTimeInfo({
  forecastTime,
  displayTimeZone,
}: {
  forecastTime: ForecastTime;
  displayTimeZone: string;
}) {
  // Several ForecastSelects are mounted at once, one per dashboard section, and react-tooltip
  // anchors by a document-wide id.
  const anchorId = React.useId();

  const {inputInitializationTimes, firstIssueTime, lastUpdatedTime} = forecastTime;
  let table: JSX.Element | null = null;
  if (inputInitializationTimes) {
    table = (
      <WeatherInputTimesTable
        inputInitializationTimes={inputInitializationTimes}
        initializationTime={forecastTime.initializationTime}
        issueTime={forecastTime.issueTime}
        displayTimeZone={displayTimeZone}
      />
    );
  } else if (firstIssueTime || lastUpdatedTime) {
    table = <ForecastTimingTable forecastTime={forecastTime} displayTimeZone={displayTimeZone} />;
  }

  if (!table) {
    return null;
  }

  return (
    <>
      <InformationCircleIcon
        className="w-5 h-5 items-center ml-1 text-gray-400 inline group-hover/forecast-time:opacity-100 opacity-0"
        data-tooltip-id={anchorId}
      />
      <Tooltip
        id={anchorId}
        className="z-[9999] max-w-md !bg-gray-50 !rounded-md"
        place="bottom"
        opacity={1}
        border="1px solid lightgray"
        positionStrategy="fixed"
      >
        <div className="text-left text-black whitespace-nowrap">{table}</div>
      </Tooltip>
    </>
  );
}

function ForecastTimingTable({
  forecastTime,
  displayTimeZone,
}: {
  forecastTime: ForecastTime;
  displayTimeZone: string;
}) {
  const {t} = useTranslations();
  const {initializationTime, firstIssueTime, lastUpdatedTime} = forecastTime;
  const showTime = (time: Date) => formatDateShortFull(time, displayTimeZone);

  // The two publish timestamps come from different clocks — the database's transaction time for the
  // first issue, the worker's floored wall clock for the last update — so a forecast published only
  // once still carries two slightly different values, occasionally in the wrong order. Call it
  // republished only when the update is later and the two don't land on the same displayed minute.
  const rows: {label: string; time: Date}[] = [{label: t('initialized'), time: initializationTime}];
  if (
    firstIssueTime &&
    lastUpdatedTime &&
    lastUpdatedTime > firstIssueTime &&
    showTime(lastUpdatedTime) !== showTime(firstIssueTime)
  ) {
    rows.push(
      {label: t('firstIssuedAt'), time: firstIssueTime},
      {label: t('updatedAt'), time: lastUpdatedTime}
    );
  } else if (lastUpdatedTime) {
    // Without a first issue time there's no telling an original publish from a later overwrite,
    // so only the pair we can date gets called an issue time.
    rows.push({label: firstIssueTime ? t('issuedAt') : t('updatedAt'), time: lastUpdatedTime});
  } else if (firstIssueTime) {
    rows.push({label: t('issuedAt'), time: firstIssueTime});
  }

  return (
    <table className="table-auto border-separate border-spacing-x-2">
      <tbody>
        {rows.map(({label, time}) => (
          <tr key={label}>
            <td className="pr-6 text-gray-500">{label}</td>
            <td className="text-right">{showTime(time)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function WeatherInputTimesTable({
  inputInitializationTimes,
  initializationTime,
  issueTime,
  displayTimeZone,
}: {
  inputInitializationTimes: NonNullable<ForecastTime['inputInitializationTimes']>;
  initializationTime: Date;
  issueTime: Date;
  displayTimeZone: string;
}) {
  const {t} = useTranslations();

  return (
    <table className="table-auto border-separate border-spacing-x-2">
      <thead>
        <tr>
          <th>{t('source')}</th>
          <th>{t('initialized')}</th>
          <th>{t('available')}</th>
        </tr>
      </thead>
      <tbody>
        <tr className="font-medium">
          <td>HydroForecast</td>
          <td>{formatDateShortFull(initializationTime, displayTimeZone)}</td>
          <td>{formatDateShortFull(issueTime, displayTimeZone)}</td>
        </tr>
        {Object.entries(inputInitializationTimes).map(([source, time]) => {
          const initTimeOffsetMinutes =
            (SERIES_PROCESSOR_IDS_TO_INIT_TIME_OFFSET_MINUTES as Record<string, number>)[source] ??
            0;
          const availableTime = DateTime.fromJSDate(time).plus({minutes: initTimeOffsetMinutes});
          return (
            <tr key={source} className="text-gray-600">
              <td>{ConfigBySourceId[source as Source]?.display || source.toUpperCase()}</td>
              <td>{formatDateShortFull(time, displayTimeZone)}</td>
              <td>{formatDateShortFull(availableTime.toJSDate(), displayTimeZone)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function getForecastTimesForDays(
  forecastTimes: ForecastTime[],
  numDays: number
): ForecastTime[] {
  if (forecastTimes.length === 0) {
    return [];
  }
  const latestForecastTimeUTCHour = forecastTimes[0].initializationTime.getUTCHours();
  return take(
    forecastTimes?.filter(
      (ft) => ft.initializationTime.getUTCHours() === latestForecastTimeUTCHour
    ),
    numDays
  );
}

// Make a date string that matches the format of the string in an html date input, which is based off
// the user's timezone and uses two digits for the day and month
function makeDateInputStringFromDate(date: Date) {
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// shortcuts either pick a list of ForecastTimes to select, or take a generic onClick
export type Shortcut = {
  displayName: string;
  getSelection: (forecastTimes: ForecastTime[]) => ForecastTime[];
  startDate?: Date;
  endDate?: Date;
};

type ShortcutProps = {
  defaultDateRange: [Date, Date];
  setDateRange: (dateRange: [Date, Date]) => void;
  setSelectedForecastTimes: (forecastTimes: ForecastTime[]) => void;
  onNextPrimaryForecastTimesChange: (
    callback: (primaryForecastTimes: ForecastTime[]) => void
  ) => void;
  forecastTimes: ForecastTime[] | undefined;
  site: Site;
  shortcuts: Shortcut[];
};

function Shortcuts({
  defaultDateRange,
  setDateRange,
  setSelectedForecastTimes,
  onNextPrimaryForecastTimesChange,
  forecastTimes,
  site,
  shortcuts,
}: ShortcutProps) {
  const {t} = useTranslations();
  let items: JSX.Element[] = [];

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  if (forecastTimes) {
    items = shortcuts.map((s, i) => {
      return (
        <button
          key={i}
          className="px-2 py-1 rounded text-blue-600 bg-blue-50"
          onClick={() => {
            setIsLoading(true);

            onNextPrimaryForecastTimesChange((forecastTimes) => {
              const selectedForecastTimes = s.getSelection(forecastTimes);
              const filteredForecastTimes = selectedForecastTimes.filter((ft) => !!ft);
              if (selectedForecastTimes.length !== filteredForecastTimes.length) {
                alert(t('notAllForecastTimesAvailable'));
              }

              setSelectedForecastTimes(filteredForecastTimes);
              setIsLoading(false);
            });

            setDateRange([s.startDate || defaultDateRange[0], s.endDate || defaultDateRange[1]]);
            recordEvent('forecast_shortcut_clicked', {shortcutName: s.displayName});
          }}
        >
          {s.displayName}
        </button>
      );
    });
  }

  const spinner: JSX.Element = isLoading ? <LoadingSpinner /> : <></>;

  return (
    <div className="flex items-center flex-wrap gap-2">
      {spinner} {items}
    </div>
  );
}
